import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { ArrowSquareOut, CheckCircle } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo, useState } from 'react';
import isEqual from 'react-fast-compare';

import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useAppSelector } from '@/redux/hooks';
import { getDefaultBidderCreditCardId } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { getCatalogPaymentProvider } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogDetailExternalUrl } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import {
    getRegistrationApproved,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getItemDetailExternalUrl } from '@/redux/modules/itemDetail';

type Props = {
    catalogId: number;
    className?: string;
    itemId?: number;
    submitRegisterForCatalog: Function;
    text?: React.ReactNode;
};

const RequestOnlineBiddingButtonComponent = ({
    catalogId,
    className,
    itemId,
    submitRegisterForCatalog,
    text = '',
}: Props) => {
    /** Only show the green request_submitted button if approved/pending AND the button has been clicked */
    const [hasClickedButton, setHasClickedButton] = useState<boolean>(false);
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const catalogExternalUrl = useAppSelector((state) => getCatalogDetailExternalUrl(state, catalogId));
    const itemExternalUrl = useAppSelector((state) => getItemDetailExternalUrl(state, itemId));
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const cardId = useAppSelector((state) => getDefaultBidderCreditCardId(state, paymentProvider));

    const externalUrl = itemId ? itemExternalUrl : catalogExternalUrl;

    return (
        <>
            {(approved || pending) && hasClickedButton ? (
                <Button
                    className={className}
                    disabled
                    variant="outlined"
                >
                    <CheckCircle
                        className="mr-2xs"
                        color="success"
                        size="sm"
                        style="fill"
                    />
                    <FormattedMessage id="request_submitted" />
                </Button>
            ) : (
                <ShouldLogInContainer
                    requireCompleteProfile
                    requireLogin
                    runFunction={(args) => {
                        setHasClickedButton(true);
                        submitRegisterForCatalog(args);
                        if (externalUrl) {
                            window.open(externalUrl, '_blank');
                        }
                    }}
                    runFunctionArgument={{ cardId, catalogId, itemId }}
                >
                    {externalUrl ? (
                        <Button className={cn('mb-sm min-w-full', className)}>
                            {text ? text : <FormattedMessage id="register_partners_site" />}
                            <ArrowSquareOut
                                className="ml-2xs"
                                size="sm"
                                style="fill"
                            />
                        </Button>
                    ) : (
                        <Button className={cn('min-w-full', className)}>
                            {text ? text : <FormattedMessage id="request_online_bidding" />}
                        </Button>
                    )}
                </ShouldLogInContainer>
            )}
            {Boolean(externalUrl) && (
                <Caption
                    className="mb-[30px]"
                    color="secondary"
                >
                    <FormattedMessage id="register_partners_site_warning" />
                </Caption>
            )}
        </>
    );
};

export const RequestOnlineBiddingButton = memo<Props>(RequestOnlineBiddingButtonComponent, isEqual);
